import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import StarIcon from '@mui/icons-material/Star';
import { SubscriptionTier } from '../types/models';

const Subscription: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // If not authenticated, redirect to home
  if (!loading && !user) {
    return <Navigate to="/" />;
  }

  if (loading || !user) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="animate-pulse flex flex-col space-y-4">
          <div className="h-12 bg-gray-800 rounded-xl w-1/3"></div>
          <div className="h-64 bg-gray-800 rounded-xl w-full"></div>
        </div>
      </div>
    );
  }

  const userTier: SubscriptionTier = user.subscriptionTier || 'free';

  const plans = [
    {
      id: 'free',
      name: 'Naia Free',
      description: 'Discover the world of VSynth music',
      price: 'Free forever',
      features: [
        'Unlimited streaming with ads',
        'Audio quality: Low (128kbps)',
        'Create up to 5 playlists',
        'Follow artists and friends',
        'Basic recommendations',
        '6 skips per hour'
      ],
      limitations: [
        'No offline listening',
        'Limited skips'
      ],
      color: 'gray',
      current: userTier === 'free'
    },
    {
      id: 'plus',
      name: 'Naia Plus',
      description: 'Enhanced listening experience',
      price: '€5.99/month',
      annualPrice: '€59.99/year (Save €11.89!)',
      features: [
        'Ad-free streaming',
        'Audio quality: Medium (320kbps)',
        'Unlimited playlists',
        'Unlimited skips',
        'Lyrics display (where available)',
        'Advanced recommendations',
        'Download up to 500 songs for offline',
        'Early access to new features'
      ],
      color: 'pink',
      current: userTier === 'plus'
    },
    {
      id: 'premium',
      name: 'Naia Premium',
      description: 'The ultimate Naia experience',
      price: '€9.99/month',
      annualPrice: '€99.99/year (Save €19.89!)',
      features: [
        'Everything in Plus',
        'Audio quality: High & Max (up to 24-bit, 192kHz)',
        'Unlimited offline downloads',
        'Exclusive content and early releases',
        'Voice bank statistics and insights',
        'Priority customer support',
        'Custom audio equalizer',
        'Crossfade and gapless playback',
        'Listen on up to 5 devices'
      ],
      color: 'gradient',
      current: userTier === 'premium'
    }
  ];

  const getPageTitle = () => {
    if (userTier === 'free') return 'Upgrade Your Experience';
    if (userTier === 'plus') return 'Subscription Management';
    return 'Subscription Management';
  };

  const getPageDescription = () => {
    if (userTier === 'free') return 'Unlock premium features and higher quality audio';
    if (userTier === 'plus') return 'Manage your Plus subscription or upgrade to Premium';
    return 'Manage your Premium subscription';
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-outfit font-bold text-white mb-4">{getPageTitle()}</h1>
        <p className="text-xl text-gray-400 mb-8">{getPageDescription()}</p>
        
        {userTier !== 'free' && (
          <div className="bg-gradient-to-r from-naia-pink/20 to-purple-600/20 border border-naia-pink/30 rounded-xl p-4 mb-8 max-w-md mx-auto">
            <div className="flex items-center justify-center gap-2 mb-2">
              <StarIcon className="text-naia-pink" />
              <span className="text-white font-medium">Current Plan</span>
            </div>
            <h3 className="text-lg font-semibold text-white capitalize">{userTier} Subscription</h3>
            <p className="text-gray-300 text-sm">
              {userTier === 'plus' ? 'Enhanced experience with ad-free streaming' : 'Ultimate Naia experience'}
            </p>
          </div>
        )}
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-8 mb-12">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`relative bg-gray-800 rounded-xl p-6 border-2 transition-all duration-300 hover:scale-105 ${
              plan.current
                ? 'border-naia-pink bg-gradient-to-br from-gray-800 to-gray-900'
                : 'border-gray-700 hover:border-gray-600'
            }`}
          >
            {plan.current && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-naia-pink text-white px-4 py-1 rounded-full text-sm font-medium">
                  Current Plan
                </span>
              </div>
            )}

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
              <p className="text-gray-400 mb-4">{plan.description}</p>
              <div className="text-3xl font-bold text-naia-pink mb-2">{plan.price}</div>
              {plan.annualPrice && (
                <div className="text-sm text-gray-400">{plan.annualPrice}</div>
              )}
            </div>

            <ul className="space-y-3 mb-8">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircleIcon className="text-green-400 mt-0.5 flex-shrink-0" fontSize="small" />
                  <span className="text-gray-300 text-sm">{feature}</span>
                </li>
              ))}
              {plan.limitations?.map((limitation, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-red-400 mt-0.5 flex-shrink-0">✗</span>
                  <span className="text-gray-400 text-sm">{limitation}</span>
                </li>
              ))}
            </ul>

            <div className="text-center">
              {plan.current ? (
                <div className="space-y-2">
                  <button className="w-full py-3 bg-gray-700 text-white rounded-xl font-medium cursor-not-allowed">
                    Current Plan
                  </button>
                  {userTier === 'premium' && (
                    <button
                      onClick={async () => {
                        try {
                          const { openCustomerPortal } = await import('../lib/stripe');
                          await openCustomerPortal();
                        } catch (error) {
                          console.error('Error opening customer portal:', error);
                          alert('Failed to open subscription management. Please try again.');
                        }
                      }}
                      className="block w-full py-2 text-gray-400 hover:text-white transition-colors text-sm"
                    >
                      Manage Subscription
                    </button>
                  )}
                </div>
              ) : (
                <button
                  className={`w-full py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                    plan.color === 'gradient'
                      ? 'bg-gradient-to-r from-naia-pink to-purple-600 text-white hover:from-pink-500 hover:to-purple-500'
                      : plan.color === 'pink'
                      ? 'bg-naia-pink text-white hover:bg-pink-500'
                      : 'bg-gray-700 text-white hover:bg-gray-600'
                  }`}
                  onClick={async () => {
                    try {
                      const { createCheckoutSession, PLAN_TYPES } = await import('../lib/stripe');
                      
                      let planType: string;
                      if (plan.id === 'plus') {
                        planType = PLAN_TYPES.PLUS_MONTHLY; // Default to monthly, could add toggle
                      } else if (plan.id === 'premium') {
                        planType = PLAN_TYPES.PREMIUM_MONTHLY; // Default to monthly, could add toggle
                      } else {
                        console.error('Invalid plan type');
                        return;
                      }
                      
                      await createCheckoutSession(planType);
                    } catch (error) {
                      console.error('Error starting checkout:', error);
                      const errorMessage = error instanceof Error ? error.message : 'Failed to start checkout. Please try again.';
                      
                      // Check if it's an authentication error
                      if (errorMessage.includes('session has expired') || errorMessage.includes('sign in')) {
                        alert(errorMessage + ' Redirecting to login...');
                        navigate('/');
                      } else {
                        alert(errorMessage);
                      }
                    }
                  }}
                >
                  <UpgradeIcon fontSize="small" />
                  {userTier === 'free' ? 'Get Started' : 'Upgrade'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Additional Info for Free Users */}
      {userTier === 'free' && (
        <div className="bg-gradient-to-r from-naia-pink/10 to-purple-600/10 border border-naia-pink/30 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Why Upgrade?</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="text-3xl mb-2">🎵</div>
              <h4 className="font-semibold text-white mb-2">Higher Quality</h4>
              <p className="text-gray-400 text-sm">Experience your favorite songs in crystal-clear quality</p>
            </div>
            <div>
              <div className="text-3xl mb-2">📱</div>
              <h4 className="font-semibold text-white mb-2">Offline Listening</h4>
              <p className="text-gray-400 text-sm">Download songs and listen anywhere, even without internet</p>
            </div>
            <div>
              <div className="text-3xl mb-2">🚫</div>
              <h4 className="font-semibold text-white mb-2">No Interruptions</h4>
              <p className="text-gray-400 text-sm">Enjoy your music without ads breaking the flow</p>
            </div>
          </div>
        </div>
      )}

      {/* Artist Account Section */}
      <div className="mt-12 bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/30 rounded-xl p-8">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-white mb-4">For Artists</h3>
          <p className="text-gray-300">Share your creations with the world</p>
        </div>

        <div className="bg-gray-800 rounded-xl p-6 max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <h4 className="text-xl font-semibold text-white mb-2">Naia Artist Account</h4>
            <div className="text-2xl font-bold text-green-400 mb-2">€4.99 one-time fee</div>
            <p className="text-gray-400 text-sm">Only paid after your artist application is approved</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {[
              'Upload unlimited songs',
              'Verified artist badge',
              'Analytics dashboard',
              'Direct fan engagement',
              'Revenue sharing eligibility',
              'Custom artist page',
              'Promotional tools',
              'Beta access to creator features'
            ].map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircleIcon className="text-green-400 mt-0.5 flex-shrink-0" fontSize="small" />
                <span className="text-gray-300 text-sm">{feature}</span>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/forartists"
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors"
            >
              Learn More
            </Link>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-gray-400 text-sm">
            <strong>Our commitment to creators:</strong> We believe in removing barriers for artists. 
            Pay only a small one-time fee after approval - no recurring costs, no upload fees, no hidden charges.
          </p>
        </div>
      </div>

      {/* Special Offers */}
      <div className="mt-12">
        <h3 className="text-2xl font-bold text-white mb-6 text-center">Special Offers</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
            <div className="text-center">
              <div className="text-3xl mb-2">🎓</div>
              <h4 className="text-xl font-semibold text-white mb-2">Student Discount</h4>
              <p className="text-gray-300 mb-4">Verified students get 50% off!</p>
              
              <div className="space-y-2 text-sm text-gray-300">
                <div><span className="font-medium">Plus:</span> €2.99/month</div>
                <div><span className="font-medium">Premium:</span> €4.99/month</div>
              </div>
              
              <p className="text-gray-400 text-xs mt-4">
                Valid student email required. Renews annually with verification.
              </p>
            </div>
          </div>

          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
            <div className="text-center">
              <div className="text-3xl mb-2">👨‍👩‍👧‍👦</div>
              <h4 className="text-xl font-semibold text-white mb-2">Family Plan</h4>
              <p className="text-gray-300 mb-4">Premium for up to 6 accounts</p>
              
              <div className="text-lg font-semibold text-purple-400 mb-2">€15.99/month</div>
              
              <span className="inline-block px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs">
                Coming Soon
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mt-12">
        <h3 className="text-2xl font-bold text-white mb-6 text-center">Frequently Asked Questions</h3>
        
        <div className="space-y-4 max-w-3xl mx-auto">
          {[
            {
              question: "Can I change plans anytime?",
              answer: "Yes! Upgrade instantly or downgrade at the end of your billing cycle."
            },
            {
              question: "What happens to my playlists if I downgrade?",
              answer: "All playlists are preserved, but Free users can only edit their first 5."
            },
            {
              question: "Do artists need a paid subscription to upload?",
              answer: "No! Artists only pay a one-time €4.99 fee after their account is approved. This gives you full upload access forever—no subscriptions or additional fees required."
            },
            {
              question: "What payment methods do you accept?",
              answer: "We accept all major credit/debit cards and PayPal through our secure payment processor, Stripe."
            }
          ].map((faq, index) => (
            <div key={index} className="bg-gray-800 rounded-xl p-6">
              <h4 className="text-white font-medium mb-2">{faq.question}</h4>
              <p className="text-gray-300 text-sm">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Methods Info */}
      <div className="mt-12 text-center">
        <p className="text-gray-400 text-sm mb-4">
          Secure payments powered by Stripe. Cancel anytime.
        </p>
        <p className="text-gray-500 text-xs">
          Prices shown in EUR. Actual charges may vary based on exchange rates and local taxes.
        </p>
      </div>
    </div>
  );
};

export default Subscription;
